// Full agent training course structure.
// Each chapter has lessons; some lessons have nested sub-lessons.
// Every lesson / sub-lesson gets its own video placeholder in the portal.

/** A block of supplementary resource content shown in a collapsible dropdown under a lesson's video. */
export interface LessonResourceBlock {
  /** Optional section heading (e.g. "EXAMFX - #1 Choice"). */
  heading?: string
  /** Paragraphs of text. URLs / domains / emails are auto-linkified. */
  paragraphs?: string[]
  /** Ordered (numbered) steps. URLs / domains / emails are auto-linkified. */
  steps?: string[]
  /** Unordered bullet points. URLs / domains / emails are auto-linkified. */
  bullets?: string[]
  /** Images to display in the block (e.g. an example business card). */
  images?: { src: string; alt?: string }[]
}

export interface Lesson {
  id: string
  title: string
  /** Optional supporting note shown under the lesson title (e.g. "Volume • Coachability • Communication"). */
  note?: string
  /** Optional YouTube video ID to embed for this lesson. */
  youtubeId?: string
  /** Optional Loom video ID to embed for this lesson. */
  loomId?: string
  /** Optional collapsible resource content shown under the lesson video. */
  resources?: LessonResourceBlock[]
  /** Optional label for the collapsible resources toggle (defaults to "Resources & Steps"). */
  resourcesLabel?: string
  /** Optional nested sub-lessons, each with their own video. */
  children?: Lesson[]
}

export interface Chapter {
  id: string
  title: string
  lessons: Lesson[]
}

export const trainingCourse: Chapter[] = [
  {
    id: 'foundation',
    title: 'Foundation',
    lessons: [
      {
        id: 'intro-to-success',
        title: 'Intro to Success',
        note: 'Volume • Coachability • Communication',
        youtubeId: 'fYn6JOY9ogk',
      },
      {
        id: 'licensing-process',
        title: 'Licensing Process',
        loomId: '5c34d9dea1354467ad1e56f0f2bc3779',
        resourcesLabel: 'Licensing Steps & Study Resources',
        resources: [
          {
            paragraphs: [
              'Follow the steps on https://nasbsalesmafia.com/licensing/',
              'NASB offers a great discount on an online life insurance exam prep course through ExamFX and WebCE.',
            ],
          },
          {
            heading: 'EXAMFX - #1 Choice',
            steps: [
              'Go to: examfx.com',
              'Click "Insurance Registration"',
              'Enter nasbsalesmafia@gmail.com where it asks for managers email address to get the discount. Then hit "Next Step"',
              '"Insurance Prelicensing" then Select your State',
              'Select "Life" and hit "Next Step"',
              'Select your Package. The $74 package includes weekly live classes where you can ask questions to a live instructor. Then hit "Next Step"',
              'Select optional add ons. Then hit "Continue"',
              'It will then take you to a login page. Hit "Create an Account"',
              "Once you've created an account it will take you to a check out screen.",
            ],
          },
          {
            heading: 'STUDY RESOURCES',
            paragraphs: [
              'Quizlet.com --> Search [your state] + "life producer exam" and it will pull up flashcards, practice tests and study guides to help you cram!',
              'StudyFetch.com --> this AI Tool can listen and read along as you go through your course to create study materials, practice tests, and reword chapters to help you learn!',
              'Chatgpt.com --> Check out this video guide to using ChatGPT to help you study.',
            ],
          },
        ],
      },
      {
        id: 'after-licensed',
        title: 'After You Get Licensed (Step-by-Step)',
        loomId: '0d6f932b5d9a43aaaa3340db8739278f',
        resourcesLabel: 'Step-by-Step Checklist',
        resources: [
          {
            paragraphs: [
              'Should be done the same day as passing the State Exam (takes 1 week avg).',
              'Tip: Have your new agent call to check on the application with the Department of Insurance multiple times a week until they receive their license #.',
              '****IMPORTANT**** Have your new agent create their NASB email NOW and use it going forward.',
              'Apply for your state license at NIPR.com — it is recommended you apply the day you pass your state test to speed up processing.',
              'GA Licensees: A notarized Citizenship Affidavit is required.',
            ],
          },
          {
            heading: 'Fingerprinting',
            paragraphs: [
              'Most states require fingerprinting. It is recommended you complete your fingerprints as soon as you pass pre-licensing.',
              "You DO NOT have to wait to complete your state test to do fingerprinting. You can find out how to schedule the fingerprinting at your state's Department of Insurance website.",
              'NOT required in: MD, ME or MI (not a complete list — check your state\u2019s requirements).',
            ],
          },
          {
            heading: 'Anti-Money Laundering (AML)',
            paragraphs: [
              'In the meantime have them begin their AML Certificate. It requires the agent to sit with the screen open on the study material for a minimum of 2 hours before allowing them to take the final test.',
              'Use http://Aml.surancebay.com to obtain your AML certificate.',
              'Choose the option "Anti-Money Laundering for the Insurance Industry".',
              'An NPN # will be required to obtain the certificate at the end of the course.',
            ],
          },
          {
            heading: 'Errors & Omissions (E&O)',
            paragraphs: [
              'After the agent gets their AML Certificate, they need to get their Errors and Omissions (E&O) Insurance Policy.',
              'https://www.napa-benefits.org/insurance/errors-and-omissions-eando-insurance',
            ],
          },
          {
            heading: 'Bank Document',
            paragraphs: [
              'Have your agent obtain a document from the bank account they plan to be paid to.',
              'It must fit these guidelines or be one of the accepted Bank Info EFT options below — carriers tend to be very specific when asking for bank information. It should be a single page, and if there is a signature field on your bank document, please make sure it is signed, as some carriers may reject it if left blank.',
              "Get your new agent a new bank account if they don't already have one set up for business (does not need to be a \u201cbusiness account\u201d).",
            ],
          },
          {
            heading: 'Voided Check',
            bullets: [
              'Can\u2019t be a "starter check".',
              'Your name & info must be pre-printed on the check, as well as the bank logo/name.',
              'Name must match the name on your life license.',
            ],
          },
          {
            heading: 'Letter on Bank Letterhead',
            bullets: [
              'Must be typed/printed (not handwritten).',
              'Includes your name, as it appears on your life license.',
              'Type of account (Checking/Savings).',
              'Account Number.',
              'Routing Number.',
              'Signed by a bank representative.',
            ],
          },
          {
            heading: 'Direct Deposit Form from the Bank',
            bullets: [
              'Not accepted by AIG unless it also has a check printed.',
              "Must have the agent's Name, Routing, and Account Number printed on it (information cannot be handwritten).",
              'Agent must sign the document for it to be valid.',
            ],
          },
        ],
      },
      {
        id: 'contracting',
        title: 'Contracting',
        resourcesLabel: 'Contracting Instructions',
        resources: [
          {
            paragraphs: [
              'For contracting, please fill out this form: https://forms.gle/NBdaMrZfWBZuvATV8',
              'Then text AZRA at +1 (469) 970-2223 with your full name and date of birth, and let her know you have completed the contracting documents. She will then send you over a link to finish the contracting process.',
            ],
          },
        ],
      },
      {
        id: 'required-apps-setup',
        title: 'Required Apps & Setup',
        children: [
          {
            id: 'knockit-pro',
            title: 'Knockit Pro',
            youtubeId: 'LrgC5aJ8enA',
            resourcesLabel: 'Download Link',
            resources: [
              {
                paragraphs: [
                  'Download Knockit Pro on the App Store: https://apps.apple.com/app/id1671150976',
                ],
              },
            ],
          },
          {
            id: 'quote-apps',
            title: 'Quote Apps',
            youtubeId: 'Be-5MNajaGA',
            resourcesLabel: 'Download Links',
            resources: [
              {
                paragraphs: ['Download the quoting apps on the App Store:'],
                bullets: [
                  'https://apps.apple.com/app/id976291481',
                  'https://apps.apple.com/app/id1533282703',
                  'https://apps.apple.com/app/id1536885388',
                  'https://apps.apple.com/app/id1510596447',
                ],
              },
            ],
          },
          { id: 'noteability', title: 'Noteability', youtubeId: 'SNLdaRacxaA' },
          {
            id: 'eapp-paper-app-walkthrough',
            title: 'E App Walkthrough',
            loomId: '2212171eb88c48cfb2780f7ca10d666f',
            resourcesLabel: 'E App Guides',
            resources: [
              {
                bullets: [
                  'MUTUAL OF OMAHA: https://drive.google.com/file/d/1-IbTqWnIP9wOaXiri3kGAh-FG8A8pY8U/view?usp=sharing',
                  'AMERICO: https://drive.google.com/file/d/1g8NnUsVIgu0j76-0M7g2HSrd0-Sq2osB/view?usp=sharing',
                  'FORESTERS: https://drive.google.com/file/d/13jpJAi7XbRO1i2UcIBt-DyLxxKfAkgtq/view?usp=sharing',
                  'COREBRIDGE: https://drive.google.com/file/d/147BAKECVnyZaM6XJBiTAKGF5tKwdo7jF/view?usp=sharing',
                  'TRANSAMERICA FE: https://drive.google.com/file/d/1qA3z157CX3legXitApLx6kfm4wwzuTc1/view?usp=sharing',
                  'LIBERTY BANKERS: https://drive.google.com/file/d/101KmcJSX6rpwmj740GmI_PD2M1kdTRQZ/view?usp=sharing',
                  'ROYAL NEIGHBORS: https://drive.google.com/file/d/19MALQezbj1cDdpfDDpMbbO67-PXxIB1Q/view?usp=sharing',
                ],
              },
            ],
          },
          {
            id: 'p2p-breakdown',
            title: 'Path to Prosperity (P2P) Breakdown',
            loomId: '71a91429d8534e26846657c7c2f350cb',
          },
        ],
      },
      {
        id: 'broker360',
        title: 'Broker360',
        children: [
          {
            id: 'b360-create-account',
            title: 'How to Create Account',
            youtubeId: 'nzD21ez9df0',
          },
          {
            id: 'b360-overview-first-steps',
            title: 'Overview & First Steps',
            youtubeId: 'QoKNZaFT1NE',
          },
          {
            id: 'b360-presentation-walkthrough',
            title: 'Presentation Walkthrough',
            youtubeId: 'B4zThe046TI',
          },
          {
            id: 'b360-activity-logging',
            title: 'Activity Logging',
            youtubeId: 'tl40TSEgy7c',
          },
          {
            id: 'b360-full-sale-log',
            title: 'Full Sale Log',
            youtubeId: 'xQCoTWysCbo',
          },
          {
            id: 'b360-mailers-add-on',
            title: 'How to Use Mailers Add-On',
            youtubeId: '86cy56uQst8',
          },
          {
            id: 'b360-faxing-feature',
            title: 'How to Use Faxing Feature',
            youtubeId: '8DYIMC6AyK0',
          },
          {
            id: 'b360-importance-logging-sales',
            title: 'Importance of Logging Sales',
            youtubeId: 'enOapkZwbKo',
          },
          {
            id: 'b360-check-ins',
            title: 'Check-Ins Explained',
            youtubeId: 'D_-OnOKT7zg',
          },
          {
            id: 'b360-life-center',
            title: 'How to Use Life Center with Broker360',
            youtubeId: 'JMBuy38r4U8',
          },
          {
            id: 'b360-stop-insurance-scams',
            title: 'Stop Insurance Scams: Text Your Clients Now',
            youtubeId: 'Br0NwN4nw7w',
          },
        ],
      },
      {
        id: 'business-cards',
        title: 'Business Cards',
        youtubeId: 'rsbhzhoaUNU',
        resourcesLabel: 'Business Card Example',
        resources: [
          {
            paragraphs: [
              'Example business card layout — follow this format for your own card.',
            ],
            images: [
              {
                src: 'https://cdn.magicpatterns.com/uploads/g6B3B4YCChr5UKDV9EYx1y/Screenshot_2026-07-03_at_2.55.42_PM.png',
                alt: 'Example NASB business card',
              },
            ],
          },
        ],
      },
      {
        id: 'memorizing-perfection',
        title: 'Memorizing Until Perfection',
        youtubeId: 'Gz1wUklDUo8',
      },
      {
        id: 'financial-game-plan',
        title: 'Financial Game Plan',
        youtubeId: 'dQzXUP00_0Q',
      },
      {
        id: 'sop-library',
        title: 'SOP Library',
        loomId: '549559fd07594b768eeb10a88a875bba',
      },
      {
        id: 'weekly-schedule',
        title: 'Weekly Schedule',
        loomId: '670c74b881ba4e71885d8036564c92db',
      },
      {
        id: 'website-walkthrough',
        title: 'Website Walkthrough',
        loomId: '354991dd99df44b888dce670f877c554',
      },
    ],
  },
  {
    id: 'leads',
    title: 'Leads',
    lessons: [
      {
        id: 'lead-types-overview',
        title: 'Lead Types Overview',
        loomId: '8c69797c314e4785b62541d2d40542f3',
      },
      {
        id: 'create-own-data-leads',
        title: 'Creating Your Own Data Leads',
        loomId: '72b84a0c0a7247f98c2b14c883f12e5b',
      },
      {
        id: 'buying-leads',
        title: 'Buying Leads',
        loomId: 'a8c632415a7841ae8071db0c5de7ec9a',
      },
      {
        id: 'territory-selection',
        title: 'Territory Selection',
        loomId: 'b5dde4c873334a96ae5cc89b5b6bb7bf',
      },
    ],
  },
  {
    id: 'door-approach',
    title: 'Door Approach',
    lessons: [
      {
        id: 'verbiage-door-approach',
        title: 'Verbiage for Door Approach',
        loomId: '749f3c61dc714b68b7ca471a5f0f7c1a',
        resourcesLabel: 'Door Approach',
        resources: [
          {
            paragraphs: [
              'Hey there, my name is Connor, I\u2019m the benefits coordinator that got assigned to you, licensed by the state! I got a form across my desk about some of your state benefits. They just sent me out to go over that with you real quick. Is there a good place we could sit down?',
            ],
          },
        ],
      },
      {
        id: 'psychology-body-language-door',
        title: 'Psychology & Body Language at the Door',
        loomId: 'cde87a54a45546c39fedf1f99ce134be',
      },
      {
        id: 'knocking-strategy',
        title: 'Knocking Strategy',
        loomId: 'faea97a9a0ff4c3aa3727bcdad135cfa',
        resourcesLabel: 'Prequalify at the Door Verbiage',
        resources: [
          {
            paragraphs: [
              'Two quick questions just to see if I even need to be here:',
            ],
            steps: [
              'We help with out of pocket expenses for seniors... are you paying out of pocket for any prescriptions?',
              'How much are you paying out of pocket for life insurance?',
            ],
          },
        ],
      },
      {
        id: 'door-rebuttals',
        title: 'Door Rebuttals',
        children: [
          {
            id: 'dr-im-busy',
            title: 'I’m Busy / This Isn’t a Good Time',
            loomId: '27bad55e1e1b4778930d4e890ab89444',
          },
          {
            id: 'dr-what-is-this-about',
            title: 'What Is This About',
            loomId: 'bf09a23bbc594ba084109173708cb41f',
          },
          {
            id: 'dr-what-benefits',
            title: 'What Benefits?',
            loomId: '02f54c8e361c430b92d9c7fe8f66d825',
          },
          {
            id: 'dr-not-interested',
            title: 'I’m Not Interested',
            loomId: '07dd8950887c4b1c95003ebb8ebca2f0',
          },
          {
            id: 'dr-already-taken-care-of',
            title: 'I Already Have Everything Taken Care Of',
            loomId: 'e73e39d2e5f840adaa0b87d749eba569',
          },
          {
            id: 'dr-no-one-told-me',
            title: 'No One Told Me You Were Coming Out',
            loomId: 'f006f67d63ad441b8e824be11b7f1697',
          },
          {
            id: 'dr-my-blank-has-to-be-here',
            title: 'My BLANK Has to Be Here',
            loomId: 'c96b8b30d9be4a419456b4510af97ddb',
          },
          {
            id: 'dr-are-you-selling-insurance',
            title: 'Are You Selling Insurance / What Are You Selling?',
            loomId: '1c7068c0c0c7424a83fea1fb2a1f493c',
          },
          {
            id: 'dr-who-are-you',
            title: 'Who Are You? / I Don’t Know Who You Are',
            loomId: '10ca7bb7dd064d2d94e0816c4e667208',
          },
        ],
      },
    ],
  },
  {
    id: 'presentation',
    title: 'Presentation',
    lessons: [
      {
        id: 'body-language-house',
        title: 'Body Language in the House',
        loomId: '4541fc10e4304e42a8accc0642b7a678',
      },
      {
        id: 'reading-the-home',
        title: 'Reading the Home',
        loomId: 'b4089ee9796f49cb956b185a98766fea',
      },
      {
        id: 'the-ten-steps',
        title: 'The Ten Steps',
        children: [
          {
            id: 'connecting-fort',
            title: 'Connecting (FORT)',
            loomId: '23ed88f2bea94158bc9b7d0fd8f8de31',
          },
          {
            id: 'credibility',
            title: 'Credibility: Verbiage & Psychology',
            loomId: '082855bf6353402e8630942f17b2ed1f',
            resourcesLabel: 'Credibility Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CMrs. Johnson this is my Colorado state license. This license is the entire state of Colorado trusting me to be out here, and if you ever need help with ANY of your benefits. Here is my name, CONNOR MATHEW HASSOUN, and here is my license number. Mrs. Johnson, this license allows me to help you out either now or if you ever need help in the future.\u201D',
                ],
              },
            ],
          },
          {
            id: 'living-benefits',
            title: 'Living Benefits: Verbiage & Psychology',
            loomId: '30eb85d5c55140fda542a1ea4cc7fec8',
          },
          {
            id: 'transition-to-life',
            title: 'Transition to Life: Verbiage & Psychology',
            loomId: '98863c31e5364d0bae187cc73920e844',
          },
          {
            id: 'policy-review',
            title: 'Policy Review: Verbiage & Psychology',
            loomId: 'cce0b86a208d4c7fa71a42d36372f709',
          },
          {
            id: 'term-life-review',
            title: 'Term Life Review',
            loomId: '62f31747509748b897b40697ca6dc967',
          },
          {
            id: 'whole-life-review',
            title: 'Whole Life Review',
            loomId: '02c2a5f2e25f4cbb8cbaf60040e9543f',
          },
          {
            id: 'universal-life',
            title: 'Universal Life',
            loomId: '86307d1839e840e7859bb693a480aea5',
          },
          {
            id: 'in-and-up-call',
            title: 'In & Up Call',
            loomId: 'd37d37d13c3248baa623363d04c8d9c0',
          },
          {
            id: 'summarize-value-riders',
            title: 'Summarize Value Riders',
            loomId: 'b5e1e12fe5fb470ebeea615ed9214463',
          },
          {
            id: 'show-price-close',
            title: 'Show Price & Close',
            loomId: '03af306da9a642c683137a44b5b8fda1',
          },
          {
            id: 'branding',
            title: 'Branding',
            loomId: '7c8d2acac0264c91aa78d16f29c296df',
          },
          {
            id: 'referrals',
            title: 'Referrals + Post Sale SOP',
            loomId: '1a6fbc3520b84b4cb8bcbe1751dcb7bb',
          },
        ],
      },
      {
        id: 'additional-presentation-training',
        title: 'Additional Presentation Training',
        children: [
          {
            id: 'banking-info-ss-verbiage',
            title: 'Banking Info + Social Security Verbiage',
            loomId: 'e96587f675cf400888bbfd73bc59510d',
            resourcesLabel: 'Banking Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CMrs. Johnson there is two things that their insurance companies need to be able to check to see if you\u2019re eligible: first they have to make sure you\u2019re healthy which we have already checked on and I\u2019ll make sure they know you don\u2019t have any serious complications. SECOND the company is going to send out all that information to you, so you can review that policy, look it over, and make sure it is something that you like. After that they have to make sure you have a valid way of paying the policy IF everything looks good when that paperwork comes out. So I just have to see something that validates that so if you can grab me a bank statement, checkbook, bank member card, or if you have the app on your phone if you can grab one of those I just have to SEE that real quick.\u201D (Immediately look down at iPad.)',
                ],
              },
            ],
          },
          {
            id: 'underwriting-during-presentation',
            title: 'Underwriting During the Presentation',
            loomId: '52c0f31ea92d4bd7b89f77499d7e8a37',
          },
          {
            id: 'fact-finding',
            title: 'Fact Finding',
            loomId: '7ba6e046f90d45b593c4ca1e4548780a',
          },
          {
            id: 'objection-prevention',
            title: 'Objection Prevention',
            loomId: '7ebe5c0370604e2abc7282b7de2976fd',
          },
          {
            id: 'handling-couples',
            title: 'Handling Couples',
            loomId: 'd23251f337dc42afbca296aa66c69672',
          },
          {
            id: 'storytelling',
            title: 'Storytelling',
            loomId: 'ec0b46194ae845168b5e79c8191eace7',
          },
          {
            id: 'creating-urgency',
            title: 'Creating Urgency',
            loomId: '641c184a5c204261baaa8a7cebe3e2b7',
          },
          {
            id: 'silence',
            title: 'Silence',
            loomId: 'ea2526c675a046c395df2f5a7df278ba',
          },
          {
            id: 'closing-psychology',
            title: 'Closing Psychology',
            loomId: '407e9d7ea34d4f2f9b35418f055fdc9c',
          },
          {
            id: 'multiple-close-techniques',
            title: 'Multiple Close Techniques',
            loomId: '05cb9071292b47e6b08963c5aab32c02',
          },
          {
            id: 'faster-vs-slower',
            title: 'When to Move Faster vs. Slow Down',
            loomId: '4478a04a2e5047a599ddee45d7013de6',
          },
          {
            id: 'saving-the-sale',
            title: 'Saving the Sale',
            loomId: 'fda58820de0643fb84365fab4c90350c',
          },
        ],
      },
      {
        id: 'sale-types',
        title: 'Sale Types',
        children: [
          {
            id: 'st-universal-life-replacement',
            title: 'Universal Life Replacement',
            loomId: 'd0003e425ca040f6a679af2468ed14ed',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CSo, fantastic, Ms. Johnson. I\u2019m glad you\u2019re with one of the companies that\u2019s in our network. I want to take a second just to explain how the three different types of life insurance work.\n\nThe first type is whole life. This policy covers you for your entire life, doesn\u2019t change on you, and it\u2019s designed to last you until the day that you die.\n\nThe second type is the term policies. These policies cover you for a period of time, but the premiums go up on you, and then the coverage cancels \u2014 so we always want to avoid those.\n\nBut the third type is a universal life policy. This is the type of policy that covers you for a very, very long time, but the issue is that it\u2019s kind of like a time bomb. In the later years of the policy, the cash value starts to get depleted, and when that cash value hits zero, the policy lapses, and you lose everything that you put into it.\n\nSo, Ms. Johnson, we want to make sure that we rescue you out of that type of situation so that you never have to risk being in a policy that doesn\u2019t cover you for your entire life.\n\nThe cool thing is, because you\u2019re in the network of state-regulated providers, you should be eligible for an upgrade to one of those super-preferred whole life programs, and my job is to check to see if you\u2019re eligible for that.\n\nSo, I\u2019m going to go ahead and check to see if you\u2019re eligible. Does that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-whole-life-replacement',
            title: 'Whole Life Replacement',
            loomId: '9da0c0f23c784208af52a53b43bc760d',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CWell, Ms. Johnson, that is fantastic that you\u2019re already within our network with one of the companies we work with \u2014 that makes our job a lot easier.\n\nThe cool thing is, because you\u2019re already in the network, you\u2019re actually entitled to an upgrade. Now, here\u2019s what that upgrade looks like.\n\nThe company you have right now works with a family \u2014 or network \u2014 of affiliated companies. And the reason they do that is so that clients like you can qualify for more coverage for the exact same amount you\u2019re paying right now.\n\nHere\u2019s how it works: the company you\u2019re with will actually upgrade you to one of their sister programs through [X Company]. With that upgrade, you\u2019ll receive additional coverage while keeping your payment the same.\n\nAnd the great part is, you may even receive some money back from your current company as part of this upgrade process.\n\nSo, Ms. Johnson, my job as the benefits coordinator is simply to check to see if you\u2019re eligible for that upgrade. And if you are, we\u2019ll go ahead and get that paperwork sent out to you.\n\nDoes that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-term-life-replacement',
            title: 'Term Life Replacement',
            loomId: '42ee87bf44a5451d9f6e48e1dede4ff3',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CMs. Johnson, that\u2019s fantastic that you\u2019re with one of the programs already within our network. So here\u2019s how it works with the type of policy you have.\n\nThere are two types of life insurance. The first is whole life. This policy covers you for your entire life \u2014 the premiums never increase, and the coverage never cancels.\n\nThe second type is called a term policy \u2014 that\u2019s what you have right now. The danger with these term policies is that they don\u2019t last for your entire life. Believe it or not, they only pay out about 2% of the time, and there are a few reasons for that.\n\nThe first reason is that the premiums actually increase every three to five years, so the policy becomes more expensive as time goes on. The second reason is that it doesn\u2019t last your entire life \u2014 every term policy, just like it sounds, covers you for a term or period of time, and then it cancels. And the third reason is that these policies don\u2019t build any cash value. So when the policy cancels or becomes unaffordable, you don\u2019t get any money back.\n\nNow, the good thing is, because you\u2019re within the network, the company you currently have will actually give you an upgrade to one of the preferred whole life programs.\n\nSo, Ms. Johnson, my job as the benefits coordinator is simply to check to see if you\u2019re eligible for that upgrade.\n\nI\u2019m going to check to see if you qualify \u2014 does that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-multiple-policy-consolidation',
            title: 'Multiple Policy Consolidation',
            loomId: '8f4dbaffc55046d09e261ef7acf100ff',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CWell, Ms. Johnson, that\u2019s fantastic that you have multiple different policies. It shows that you really do want to protect your family, which is amazing.\n\nThe cool thing about how life insurance works \u2014 similar to other things \u2014 is that it actually becomes cheaper when you purchase it in bulk rather than individually.\n\nNow, in your situation, having all these different policies can become problematic for a couple of reasons. Number one, it gives you a lot of different payments you have to manage. Number two, if anything ever goes wrong, you have to update multiple companies. And number three, it can create challenges for your family later on \u2014 because when you pass, they\u2019ll have to contact and deal with multiple companies to receive those benefits.\n\nBut there\u2019s a great solution. Because you\u2019re already within the network and you\u2019re eligible for an upgrade, what these companies will do is consolidate your coverage down into one.\n\nThat\u2019s going to do a few things for you \u2014 it\u2019s going to make your life easier because you\u2019ll only have one policy to manage, it\u2019s going to give you more overall coverage, it\u2019ll put some money back in your pocket, and it\u2019ll make things much easier for your family when the time comes because they\u2019ll only have to deal with one company instead of several.\n\nSo my job, Ms. Johnson, is to check to see if you\u2019re eligible for that. Does that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-whole-life-reduced-paid-up',
            title: 'Whole Life + (Reduced Paid-Up)',
            loomId: 'e19005aac9014628acccf3d12bb101cd',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CSo, Ms. Johnson, that\u2019s fantastic that you\u2019re with one of the companies in our network, and I\u2019ve got some good news for you.\n\nBecause you\u2019ve been with this company for as long as you have, you actually have the ability \u2014 or the eligibility \u2014 to get more coverage for the exact same amount that you\u2019re paying right now.\n\nHere\u2019s how it works: with the current policy you have, you\u2019ll actually be able to stop paying on that, and it will convert into paid-up life insurance.\n\nSo, you\u2019ll have a portion of your life insurance over here with your current company, and then you\u2019ll have a portion of your life insurance with one of the new preferred programs.\n\nNow, your overall coverage is going to be more than what you have right now, and you\u2019ll still be paying the exact same amount \u2014 with the option to increase it even further down the road if you want or need to.\n\nMy job, Ms. Johnson, is simply to check to see if you\u2019re eligible for that. Does that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-clean-sheeted-policy',
            title: 'Clean Sheeted (Misstated) Policy',
            loomId: '0ad5665b0ae8413483aa6af077903bea',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CAwesome, Ms. Johnson. Well, it\u2019s fantastic that you\u2019re with one of the companies that are in our network.\n\nNow, I\u2019m going to tell you something that most people aren\u2019t going to tell you \u2014 but it\u2019s something that can protect your family both now and down the road.\n\nThere\u2019s something called a clean-sheeted or misstated insurance application. What that means is the agent who originally wrote this policy misstated one of the questions on the application \u2014 it could have been the smoking class, a health question, or something else along those lines.\n\nThe problem is that when you pass, the way this policy is currently structured, it\u2019s not going to pay out to your family.\n\nNow, that\u2019s the bad news \u2014 but here\u2019s the good news: because you\u2019re within the network, my job is to get this corrected so that the policy you have is guaranteed to your family, it\u2019s not clean-sheeted, and everything is properly stated just the way it needs to be.\n\nSo, what I\u2019m going to do is check to see if you\u2019re eligible for that upgrade. And if you are, I\u2019ll go ahead and get that paperwork sent out to you.\n\nDoes that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-accidental-policy-replacement',
            title: 'Accidental Policy Replacement',
            loomId: 'c41a208dd3e34a2fb205691744f4e0b5',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CWell, that\u2019s fantastic, Ms. Johnson, that you\u2019re with one of the companies in our network and that you already have coverage in place.\n\nNow, here\u2019s how it works with the type of policy you have. You currently have what\u2019s called an accidental life insurance policy. What that means is that the policy only pays out in the event of an accidental death.\n\nNow, you might think that would be a problem \u2014 but it\u2019s actually not a problem in your case, and here\u2019s why.\n\nBecause you\u2019re within the network, the company you have will actually give you an upgrade to one of the whole life programs that already has that accidental coverage built in, virtually at no additional cost.\n\nMy job is to check to see if you\u2019re eligible for that upgrade, and if you are, I\u2019ll go ahead and get that paperwork sent out to you.\n\nDoes that kind of make sense?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-agent-broker-replacement',
            title: 'Agent or Broker Replacement',
            loomId: '810d97e453da4f58863051c52b3f2f25',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CSo, Ms. Johnson, in the situation you\u2019re in, one of the most important things is making sure that both the company and the person servicing your policy are extremely accessible to you and your family.\n\nNow, in your case, because the agent you originally worked with has moved or is no longer helping you with questions and updates, it\u2019s my job to make sure you\u2019re upgraded into one of the preferred programs under someone like myself \u2014 someone who can actually service your policy and be available when you need help.\n\nSo, what I\u2019m going to do is make sure that you receive that upgrade to one of the preferred programs, and from this point forward, I\u2019ll be your direct contact for anything you need \u2014 questions, changes, or anything related to your policy.\n\nNow, Ms. Johnson, does that make sense? Do you have any questions about that?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-loan-forgiveness',
            title: 'Loan Forgiveness',
            loomId: 'fcbf7b1da6414207877ba9f84ca42ce7',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CWell, Ms. Johnson, it\u2019s fantastic that you\u2019re with one of the companies in our network. And even though we found out about that loan you have against the policy, I\u2019ve got some good news for you.\n\nBecause you\u2019re within the network, these companies have recently introduced something called a Loan Forgiveness Program.\n\nHere\u2019s what that means: whenever you have a loan against your policy, that loan actually decreases both your cash value and your death benefit. What these life insurance companies will do through this program is upgrade you to one of their sister companies or super-preferred programs.\n\nAnd the great part is, instead of having to pay that loan back, you won\u2019t have to repay it. In fact, you\u2019ll actually get a little money back in your pocket, and your total coverage amount will increase because you\u2019ll no longer be carrying that loan.\n\nSo, my job, Ms. Johnson, is to make sure that you get approved for that Loan Forgiveness Program. And if you are, we\u2019ll go ahead and get the paperwork sent out to you for that upgraded program.\n\nDo you have any questions about that, or does that make sense to you?\u201D',
                ],
              },
            ],
          },
          {
            id: 'st-new-business-sale',
            title: 'New Business Sale',
            loomId: '154564ac088c444fb7a1a0ba6ea67157',
            resourcesLabel: 'Verbiage',
            resources: [
              {
                paragraphs: [
                  '\u201CAwesome, Ms. Johnson. Well, because you don\u2019t have anything in place right now \u2014 and that\u2019s something you\u2019re looking to get \u2014 my job, as kind of the information guy, is to check to see if you\u2019re eligible for one of the super-preferred programs to help cover your family.\n\nNow, if you are eligible for that, what I\u2019m going to do is get that paperwork sent out to you, and we\u2019ll be able to review it together to make sure that it\u2019s both affordable for you and enough coverage for your family.\n\nOkay, does that kind of make sense, Ms. Johnson?\u201D',
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'rebuttals',
    title: 'Rebuttals',
    lessons: [
      {
        id: 'r-think-about-it',
        title: 'I Need to Think About It',
        loomId: '16aa4d9b54a24c07a86ba6aeb044904c',
      },
      {
        id: 'r-too-expensive',
        title: 'That’s Too Expensive',
        loomId: '38c95157bec841a989cab01b8de00083',
      },
      {
        id: 'r-shop-around',
        title: 'I Want to Shop Around',
        loomId: 'd9a41ed60d914e4da4f79aaf0def0f8c',
      },
      {
        id: 'r-dont-give-info',
        title: 'I Don’t Give Out My Information',
        loomId: '84b681ea573f48bd90e5dba742d7f86e',
      },
    ],
  },
  {
    id: 'back-office',
    title: 'Back Office',
    lessons: [
      {
        id: 'calling-carriers',
        title: 'Calling Carriers: What to Ask & When',
        loomId: '1c225cb27b6649efa1b9af086021c0a9',
      },
      {
        id: 'client-follow-up',
        title: 'Client Follow-Up',
        loomId: '0debb63f230f4ce3ba48d1791ee800a1',
      },
      {
        id: 'crm-organization',
        title: 'CRM Organization',
        loomId: 'a6a730c8dbc04f0d983d20ad14036585',
      },
      {
        id: 'lead-management',
        title: 'Lead Management',
        loomId: 'e31c03f1b9e944c8940b4e10b9ccb4a0',
      },
      {
        id: 'money-management',
        title: 'Money Management',
        loomId: '5e262ca5b1a042e487c4cea7c6cebe81',
      },
    ],
  },
  {
    id: 'mindset',
    title: 'Mindset',
    lessons: [
      {
        id: 'handling-rejection',
        title: 'Handling Rejection',
        loomId: '7d33fa758db44f9083726b5971a72848',
      },
      {
        id: 'consistency',
        title: 'Consistency',
        loomId: '97cf33d017964e3994b6120ec74a8e82',
      },

      {
        id: 'confidence',
        title: 'Confidence',
        loomId: '0c39bce4a3fc46ad9104fbf423d47a46',
      },
      {
        id: 'when-everything-clicks',
        title: 'When Everything Clicks',
        loomId: 'a8d4da6c526c4472bb74cdc1d2eaf07e',
      },
    ],
  },
  {
    id: 'recruiting',
    title: 'Recruiting',
    lessons: [
      {
        id: 'recruiting-mindset',
        title: 'Recruiting Mindset',
        loomId: 'b8c916d1e82149ba925cfe0eed13b100',
      },
      {
        id: 'cold-recruiting',
        title: 'Cold Recruiting',
        loomId: 'de474e25640645089905872dc70c2a90',
      },
      {
        id: 'warm-recruiting',
        title: 'Warm Recruiting',
        loomId: '2984dae03fd04b679030fc58dcb545e5',
      },
      {
        id: 'money-market',
        title: 'Money Market',
        loomId: '79c79b23e15c47358e2f55a2f0962026',
      },
      {
        id: 'ride-alongs',
        title: 'Ride-Alongs',
        loomId: '4c6fad77a167469591cc6cc56abaa877',
      },
      {
        id: 'interviewing',
        title: 'Interviewing',
        loomId: 'b046dbedde6346299685eca1662166b6',
      },

      {
        id: 'recruiting-follow-up',
        title: 'Recruiting Follow-Up',
        loomId: '8ad615fc7ebd4aa286edd7f1ff0d9620',
      },
      {
        id: 'in-up-training',
        title: 'In & Up Training',
        loomId: 'e482fce0be4f44d9998e90ee8c09952e',
      },
    ],
  },
  {
    id: 'leadership',
    title: 'Leadership',
    lessons: [
      {
        id: 'agent-check-ins',
        title: 'Agent Check-Ins',
        loomId: '2a48f9c4d3ad4b59be2c8cfefdcbdcf6',
      },
      {
        id: 'building-culture',
        title: 'Building Culture',
        loomId: '556fd792ff194fb489ed87c3f9853c3d',
      },
      {
        id: 'training-new-agents',
        title: 'Training New Agents',
        loomId: '526ce216ac84440893b9bebd51b2e260',
      },
      {
        id: 'communication',
        title: 'Communication',
        loomId: '8debb226c2114425944fabf95d866c0b',
      },
      {
        id: 'promotions',
        title: 'Promotions',
        loomId: 'c1d544979bf5435fae9c070164ac5dd3',
      },
      {
        id: 'building-leaders',
        title: 'Building Leaders',
        loomId: '119dbe41751b4bb8a94649d367258399',
      },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    lessons: [
      {
        id: 'res-morning-call',
        title: 'Morning Call',
        resourcesLabel: 'Morning Call Details',
        resources: [
          {
            paragraphs: [
              'Call Number: 719-500-5079\n\nMonday\u2013Friday: 8:00 AM EST / 7:00 AM CST\n\nPlease keep your phone muted if you are not actively speaking on the call.\n\nIf you wrote over $2,000 AP, there is a possibility that you will be called on to speak about your day. Please be prepared to tell us about how you got those sales.\n\nIf you are having issues connecting to the conference call line, you can text "HELP" to our call number, 719-500-5079, for an alternate number to connect, if needed.',
            ],
          },
        ],
      },
      {
        id: 'res-one-on-one-training-calls',
        title: '1 on 1 Training Calls',
        resourcesLabel: 'Schedule a 1 on 1 Training Call',
        resources: [
          {
            bullets: [
              'SCHEDULE A CALL WITH CONNOR (BUFF): https://calendly.com/connornasb/training-zoom',
              'SCHEDULE A CALL WITH CONNOR TISDALE: https://calendly.com/connortisdalenasb/connor-tisdale-training-zoom?month=2026-07',
            ],
          },
        ],
      },
      {
        id: 'res-e-app-guide',
        title: 'E App Guide',
        resourcesLabel: 'E App Guides',
        resources: [
          {
            bullets: [
              'MUTUAL OF OMAHA: https://drive.google.com/file/d/1R3U4A5rgjpyK-CmuyFuDG9xvLUCYevxB/view?usp=drive_link',
              'AMERICO: https://drive.google.com/file/d/1RalXTBOXpFrNobJQ0pJIiBmLpTcW0PTY/view?usp=drive_link',
              'FORESTERS: https://drive.google.com/file/d/1tQmxBGOod9N_VbOeA-xjZBAeaAi-xyha/view?usp=drive_link',
              'COREBRIDGE: https://drive.google.com/file/d/1iLkdftQd7O7ewLf9NEp5IJOcygb1HVUY/view?usp=drive_link',
              'TRANSAMERICA FE: https://drive.google.com/file/d/1Y4JoYqSwjUOY3_L0M-v0sEN4GprQvgQE/view?usp=drive_link',
              'LIBERTY BANKERS: https://drive.google.com/file/d/1koI4IoQ3iW2E9DNUne_0xYxMujJoCX9e/view?usp=drive_link',
              'ROYAL NEIGHBORS: https://drive.google.com/file/d/1bScNjuaa4MsP-qNAK2J5QuSerh_f1vMM/view?usp=drive_link',
              'TRANSAMERICA IMMEDIATE SOLUTION: https://drive.google.com/file/d/1EUTewyzVpOkslsrOEVjLniPZRdgzElVK/view?usp=drive_link',
            ],
          },
        ],
      },
      {
        id: 'res-educational-youtube-channels',
        title: 'Educational YouTube Channels',
        resourcesLabel: 'Recommended Channels',
        resources: [
          {
            heading: 'Charisma on Command',
            paragraphs: [
              'Communication, confidence, body language, charisma, and persuasion using real-world examples.\nhttps://www.youtube.com/@Charismaoncommand',
            ],
          },
          {
            heading: 'Vinh Giang',
            paragraphs: [
              'Public speaking, storytelling, vocal mastery, and becoming a captivating communicator.\nhttps://www.youtube.com/@askvinh',
            ],
          },
          {
            heading: 'Alex Hormozi',
            paragraphs: [
              'Sales psychology, business growth, value creation, offers, leadership, hiring, and scaling companies.\nhttps://www.youtube.com/@AlexHormozi',
            ],
          },
          {
            heading: 'Jeremy Miner',
            paragraphs: [
              'Modern, low-pressure sales conversations, objection handling, and human psychology.\nhttps://www.youtube.com/@JeremyMinerNEPQ',
            ],
          },
          {
            heading: 'Chris Voss',
            paragraphs: [
              'Negotiation techniques from a former FBI hostage negotiator that apply directly to sales.\nhttps://www.youtube.com/@ChrisVossOfficial',
            ],
          },
          {
            heading: 'Simon Sinek',
            paragraphs: [
              'Leadership, culture, trust, purpose, and building organizations people want to follow.\nhttps://www.youtube.com/@SimonSinek',
            ],
          },
          {
            heading: 'Jocko Podcast',
            paragraphs: [
              'Extreme ownership, discipline, leadership, accountability, and mental toughness.\nhttps://www.youtube.com/@JockoPodcastOfficial',
            ],
          },
          {
            heading: 'Brian Tracy',
            paragraphs: [
              'Timeless sales fundamentals, prospecting, goal setting, confidence, and closing.\nhttps://www.youtube.com/@BrianTracyOfficial',
            ],
          },
          {
            heading: 'Tom Hopkins',
            paragraphs: [
              'Relationship selling, asking questions, objection handling, and professionalism.\nhttps://www.youtube.com/@TomHopkinsInternational',
            ],
          },
          {
            heading: 'Zig Ziglar',
            paragraphs: [
              'Classic motivation, ethics, communication, and sales fundamentals that never get old.\nhttps://www.youtube.com/@ZigZiglarInc',
            ],
          },
          {
            heading: 'Grant Cardone',
            paragraphs: [
              'Energy, prospecting, follow-up, confidence, and high-volume selling. Best for developing intensity.\nhttps://www.youtube.com/@GrantCardone',
            ],
          },
          {
            heading: 'Jordan Peterson',
            paragraphs: [
              'Responsibility, articulation, confidence, and understanding human behavior.\nhttps://www.youtube.com/@JordanBPeterson',
            ],
          },
          {
            heading: 'The Diary of a CEO',
            paragraphs: [
              'Long-form interviews with world-class entrepreneurs, athletes, psychologists, and leaders.\nhttps://www.youtube.com/@TheDiaryOfACEO',
            ],
          },
          {
            heading: 'Impact Theory',
            paragraphs: [
              'Mindset, entrepreneurship, leadership, and interviews with high performers.\nhttps://www.youtube.com/@TomBilyeu',
            ],
          },
          {
            heading: 'Stanford Graduate School of Business',
            paragraphs: [
              'Executive leadership, management, communication, and decision-making from world-class professors.\nhttps://www.youtube.com/@stanfordgsb',
            ],
          },
        ],
      },
      {
        id: 'res-business-cards',
        title: 'Business Cards',
        resourcesLabel: 'Business Cards',
        resources: [
          {
            images: [
              {
                src: 'https://cdn.magicpatterns.com/uploads/fKEhmDEQdiA5pGVoF5ngsP/image.png',
                alt: 'NASB business cards, magnets and thank you cards pricing sheet with ordering instructions',
              },
            ],
          },
        ],
      },
      {
        id: 'res-free-carrier-supplies',
        title: 'FREE Carrier Supplies',
        resourcesLabel: 'Carrier Supply Ordering',
        resources: [
          {
            paragraphs: [
              'Carriers will ship you supplies, for free, once you are contracted with them. You can order supplies such as Brochures, Folders, Paper Apps, Memorial Guides etc.',
            ],
          },
          {
            heading: 'Aetna/CVS',
            paragraphs: [
              'Can call Agent Services: 866-272-6630\nWebsite: https://www.aetnaseniorproducts.com/ssi/login.fcc\nLog-in > Order Supplies & Download Forms > Click orange Order Supplies/Download Forms button >\n\nSelect your State: Enrollment Kits: State-Specific Paper App Packets Items > ACC - Final Expense: Tri-Fold Brochure\n\nScroll down below the states to "Assorted Items" to order Pocket Folders',
            ],
          },
          {
            heading: 'American General (AIG)',
            paragraphs: [
              'Agent Support: 800-677-3311\nAgent Portal Connext: https://live.cloud.api.aig.com/life/connext-portal/app/home\nSupplies: Go to Connext. Click on "Form Depot" on top right of screen, by your name. Download pdf\u2019s, have emailed to you or have supplies mailed. AIG apps are agent-specific and will have your name, agent number and a unique bar code printed on them.',
            ],
          },
          {
            heading: 'Americo',
            paragraphs: [
              'Agent Support: 800-231-0801\nAgent Portal: https://portal.americoagent.com/\nProduct Resources: Final Expense > Eagle Premier > Sales Tools/See All Sales Tools > Select Your State. If "Add to Cart" is available, then that is an item that can be ordered and shipped to you.',
            ],
          },
          {
            heading: 'American-Amicable (AmAm)',
            paragraphs: [
              'Agent Support: 800-736-7311 or Live Chat on Website\nAgent Portal: https://www.americanamicable.com/Marketing/area/A/marketing.php\nSupplies: Log in. Click "Order Supplies" button. Select the state and product. American Guardian & American Legacy are the Final Expense products. Depending on the state, the apps/supplies may be on either the AmAm site or on the Occidental site. Enter the number under "Quantity" click "Continue" to review your order then click "Place Order" and enter your shipping information.',
            ],
          },
          {
            heading: 'Baltimore',
            paragraphs: [
              'Agent Support: 877-533-2468\nAgent Portal: https://agentportal.baltlife.com\nSupplies: App packs for download on site or call for supplies. Select "Silverguard", "Forms" and "Application Package" for "Single Premium Whole Life".',
            ],
          },
          {
            heading: 'Columbian Financial Group (CFG)',
            paragraphs: [
              'Agent Support: 800-305-1335\nAgent Portal: https://www.cfglife.com/producer-login\nSupplies: Download on site or Fax order-form to 607-724-4345 or email supplyorders@cfglife.com',
            ],
          },
          {
            heading: 'Foresters',
            paragraphs: [
              'Agent Support: 866-466-7166\nAgent Portal ezbiz: https://portal.foresters.biz\nSupplies: Call Sales & Marketing to order or download on web under "Forms & Brochures" (Planright). If faxing apps, must include their FAX Cover page.',
            ],
          },
          {
            heading: 'Gerber',
            paragraphs: [
              'Agent Support: 1-800-428-4947\nAgent Portal: https://www.gerberlife.com/gl/ap/view/agency/login.jsp\nSupplies: Call to order or download on the web. Click "Home Dashboard", find "Guaranteed Life Insurance" under "Product Information" and click on "applications & rate cards".',
            ],
          },
          {
            heading: 'Great Western',
            paragraphs: [
              'Agent Support: 866-689-1404\nAgent Portal MyNavigator: https://my.gwic.com/online/\nSupplies: Order Supplies: Final Expense. Call to order or download online. Browse Catalog',
            ],
          },
          {
            heading: 'Liberty Bankers Life (LBL)',
            paragraphs: [
              'Agent Support: 888-525-4467\nAgent Portal: https://www.libertybankerslife.com/mylbl/logon.php\nSupplies: Log on > My Briefcase > Docs & Tools > Premier Pro - Supply Ordering. The log-in instructions are in a box that you click and it will take you to another site to order.',
            ],
          },
          {
            heading: 'Mutual of Omaha/United of Omaha',
            paragraphs: [
              'Agent Support: 800-693-6083\nAgent Portal SPA: https://accounts.mutualofomaha.com/\nSupplies: Call to order or download. Go to "Sales Tools", "Forms & Materials". Select Company "United of Omaha", "New Business" choose your state and then "Whole Life". (Living Promise & Children\u2019s Whole Life).',
            ],
          },
          {
            heading: 'Occidental Life',
            paragraphs: [
              'Agent Support: 800-736-7311 or Live Chat on their website\nAgent Website: https://www.occidentallife.com/Marketing/area/A/marketing.php\nSupplies: Log in. Click "Order Supplies" button. Select the state and product. American Guardian & American Legacy are the Final Expense products. Depending on the state, the apps/supplies may be on either the AmAm site or on the Occidental site.',
            ],
          },
          {
            heading: 'Royal Neighbors',
            paragraphs: [
              'Agent Support: 800-770-4561\nAgent Portal agent.RoyalNeighbors.org\nSupplies: Order printed supplies online or download pdfs',
            ],
          },
          {
            heading: 'Transamerica',
            paragraphs: [
              'Supplies: Call 866-545-9058 to order brochures/supplies.',
            ],
          },
        ],
      },
      {
        id: 'res-uw-resources',
        title: 'UW Resources',
        resourcesLabel: 'Underwriting Guides & Drug Lists',
        resources: [
          {
            bullets: [
              'Sales Mafia UW Cheat sheet: https://nasbsalesmafia.com/uw-cheatsheet/',
              'AIG UW GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2022/12/AIG-SIWL-UW-Guide.pdf',
              'AMERICO UW GUIDE: https://americofinalexpense.com/ES/AgentGuide.pdf',
              'AETNA DRUG GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2020/06/Accendo-Final-Expense-Drug-List.pdf',
              'FORESTERS UW GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Foresters-PlanRight-Medical-Guide.pdf',
              'LIBERTY DRUG GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2020/03/LBL-Drug-Guide.pdf',
              'MUTUAL DRUG GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Omaha-Rx-Guide.pdf',
              'OCC/AMAM UW GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2022/02/AmAm-Agent-Guide.pdf',
              'ROYAL DRUG GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2020/04/Royal-Neighbors-Drug-Guide.pdf',
              'TRANS FE UW GUIDE: https://cdn.bfldr.com/86JM1UOD/as/m7q69ngk78kw39p7w5923h/Transamerica_FE_Express_Solution_Agent_Guide',
              'TRANS IMM SOL UW GUIDE: https://nasbsalesmafia.com/wp-content/uploads/2024/01/Trans-Agent-Guide-01.24.pdf',
              'BUILD CHART: https://nasbsalesmafia.com/build-chart/',
            ],
          },
        ],
      },
      {
        id: 'res-senior-centers',
        title: 'Senior Centers',
        resourcesLabel: 'Senior Center Resources',
        resources: [
          {
            paragraphs: [
              'About Senior Centers and some Tips for hosting events.',
            ],
            bullets: [
              'NASB Tablecloths can be purchased on the NASB Store',
              'Senior Center Presentation Script Example',
              'About NASB Printable Flyer',
              'Sign-In Sheet1 - Sign-in Sheet with age and Insurance Company',
              "Sample Flyer PDF - Sample Flyer, word doc, download to edit - Reach out to Val if you'd like help with flyers.",
              'Call Recording: Senior Center Training w/Evan & Blair',
              'Call Recording: Senior Center Tips from Naomi G',
            ],
          },
        ],
      },
      {
        id: 'res-broker360-youtube',
        title: 'Broker 360 YouTube Channel',
        resourcesLabel: 'Broker 360 YouTube',
        resources: [
          {
            bullets: ['www.youtube.com/@Broker360App'],
          },
        ],
      },
      {
        id: 'res-knockit-youtube',
        title: 'Knock It Pro YouTube',
        resourcesLabel: 'Knock It Pro YouTube',
        resources: [
          {
            bullets: [
              'Knock It! Pro - Getting Started Guide #1: https://youtu.be/7I6kMvg2xrk?si=8HgOLYJ_KPYq4uXY',
              'Knock It! Pro - Getting Started Guide #2: https://youtu.be/Gsv22xPlZAM?si=iNnR_FxLiqlWG4xD',
              'Maximize Productivity Stop by Leads on Your Route: https://youtu.be/jkQFEKQaZNA?si=1BiKD7U_HROwfHgx',
              'Importing From Integrity Lead Center: https://youtu.be/9ceCejCLpCU?si=JRnyCTrYBjmqFIma',
            ],
          },
        ],
      },
      {
        id: 'res-veteran-benefits',
        title: 'Veteran Benefits',
        resourcesLabel: 'Veteran Benefit Resources',
        resources: [
          {
            bullets: [
              'VETERAN BURIAL BENEFITS SHEETS: https://nasbsalesmafia.com/wp-content/uploads/2020/09/Veteran-Benefits.pdf',
              'Veteran Burial and Plot-Interment Allowances: https://nasbsalesmafia.com/wp-content/uploads/2022/03/VA-Benefits-Unit.pdf',
              'Veteran Group Life Ins (VGLI and SVI): https://www.va.gov/life-insurance/options-eligibility/vgli/',
            ],
          },
        ],
      },
      {
        id: 'res-commissions-promotions',
        title: 'Commissions & Promotions',
        resourcesLabel: 'Commission & Promotion Charts',
        resources: [
          {
            bullets: [
              'PATH 2 PARTNER: https://nasbsalesmafia.com/wp-content/uploads/2021/10/P2P-2021-04-Comission-Chart.pdf',
              'COMMISSION CHART BY P2P LEVEL: https://nasbsalesmafia.com/wp-content/uploads/2023/05/2023compsheet.pdf',
              'COMMISSION ADJUSTMENT CHART: https://nasbsalesmafia.com/wp-content/uploads/2023/04/Commission-Adjustments-2026.pdf',
              'HOW NUMBERS ARE COUNTED BY CARRIER: https://nasbsalesmafia.com/wp-content/uploads/2021/01/How-Numbers-Are-Reported-01.2021-pdf.pdf',
            ],
          },
        ],
      },
      {
        id: 'res-binder-printout',
        title: 'Binder Printout',
        resourcesLabel: 'Binder Printout & RX Card',
        resources: [
          {
            bullets: [
              'BINDER PRINTOUT: https://nasbsalesmafia.com/wp-content/uploads/2025/01/Sales-Binder-Print-Out.pdf',
              'WELL RX CARD: https://nasbsalesmafia.com/wp-content/uploads/2020/03/DISCOUNTCARD-Flyer-update-2020.pdf',
            ],
          },
        ],
      },
      {
        id: 'res-outstanding-requirements',
        title:
          'Pulling Outstanding Requirements / Policy Conservation Opportunities',
        resourcesLabel: 'Conservation Steps by Carrier',
        resources: [
          {
            heading: 'Mutual of Omaha',
            paragraphs: [
              'Home Screen\n\u201CMy Business\u201D\n"Conservation Events"\n\nYou will be able to see all of your policies that are lapsed, cancelled, or will potentially lapse \u2013 Very user friendly and easy to use \u2013 You can even filter for events such as \u201CCancellations, Lapse Notices, Updates\u201D, etc. and then download as a CSV',
            ],
          },
          {
            heading: 'Royal Neighbors',
            paragraphs: ['Home Screen\n\u201CReports\u201D\n"Lapse Report"'],
          },
          {
            heading: 'TransAmerica',
            paragraphs: [
              'Home Screen\nAgent Net Info\n"Pending Reports"\n"Pending Requirements Report"',
            ],
          },
          {
            heading: 'TransAmerica FEXB Express',
            paragraphs: [
              'Home Screen\nTransAmerica Life Access\n"View My Book"\n"Inforce" \u2013 You will see all of your policies and their statuses. You can click on \u201CExpand Filters\u201D and search for \u201CLapsed\u201D or \u201CTerminated\u201D policies, amongst many other choices',
            ],
          },
          {
            heading: 'AIG/Corebridge',
            paragraphs: [
              'Home Screen\n"My Business"\n"Inforce"\n\nJust like with TransAmerica FEXB express, you will see all of your policies and their statuses, and you are able to filter the statuses of your policies and do advanced searches',
            ],
          },
          {
            heading: 'Americo',
            paragraphs: ['Home Screen\n"Work List"'],
          },
          {
            heading: 'Foresters',
            paragraphs: [
              'Home Screen\n"My Biz"\n"Reports"\n"Pending and Recent Lapse Report\u201D\n\u201CGenerate Report\u201D',
            ],
          },
          {
            heading: 'American Amicable',
            paragraphs: [
              'Home Screen\n\u201CAgent Efile"\nClient List"\n"View all CSV"\n\nOnce the document downloads, you can sort by the status of each policy',
            ],
          },
        ],
      },
      {
        id: 'res-money-management',
        title: 'Money Management',
        resourcesLabel: 'Money Management Resources',
        resources: [
          {
            bullets: [
              'Business Account Flow Chart: https://nasbsalesmafia.com/wp-content/uploads/2025/01/Business-Bank-Account-Chart.jpg',
              'Business Budget Sheet: https://nasbsalesmafia.com/wp-content/uploads/2020/06/Business-Budget-Template.pdf',
              'Personal Budget Sheet: https://nasbsalesmafia.com/wp-content/uploads/2020/06/Personal-Budget-Template.pdf',
              'WHAT TO SAVE FOR TAXES: https://nasbsalesmafia.com/wp-content/uploads/2020/06/What-to-save-for-Taxes.pdf',
            ],
          },
        ],
      },
      {
        id: 'res-competitor-sample-policies',
        title: 'Competitor Sample Policies',
        resourcesLabel: 'Sample Policies & Annuities',
        resources: [
          {
            bullets: [
              'AARP Group Whole Life Sample Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Sample-Policy-AARP-GP.pdf',
              'AARP Term Sample Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Sample-Policy-AARP-Term.pdf',
              'AIL GRADED POLICY: https://nasbsalesmafia.com/wp-content/uploads/2021/06/AIL-4-YEAR-GRADED.pdf',
              'BANKERS LIFE GRADED POLICY: https://nasbsalesmafia.com/wp-content/uploads/2021/06/Bankers-Life-Graded.pdf',
              'Colonial Penn Sample Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/04/Colonial-Penn-policy-.pdf',
              'Globe Life Sample Term Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Sample-Policy-Globe-Life.pdf',
              'Lincoln Heritage Sample Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Sample-Policy-Lincoln-Heritage-Graded-Policy.pdf',
              'Mutual of Omaha Graded Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Omaha-Graded.pdf',
              'Senior Life Sample Policy: https://nasbsalesmafia.com/wp-content/uploads/2020/03/Senior-Life-Policy-Sample.pdf',
            ],
          },
          {
            heading: 'Annuities',
            bullets: [
              'Annuity Fact Finder: https://nasbsalesmafia.com/wp-content/uploads/2021/02/NASB_Annuity_Fact_Finder.pdf',
              'Annuity Tips: https://nasbsalesmafia.com/wp-content/uploads/2021/02/NASB_Annuity_Tips.pdf',
              'Annuity Suitability: https://nasbsalesmafia.com/wp-content/uploads/2021/02/NASB_Annuity_Suitability_Tips.pdf',
              'Annuity Decision Tree: https://nasbsalesmafia.com/wp-content/uploads/2021/02/NASB_Annuity_Decision_Tree.pdf',
              'Annuity Client Flyer: https://nasbsalesmafia.com/wp-content/uploads/2021/02/NASB_Annuity_Client_Flyer.pdf',
            ],
          },
        ],
      },
      {
        id: 'res-carrier-forms',
        title: 'Carrier Forms',
        resourcesLabel: 'Carrier Forms Library',
        resources: [
          {
            heading: 'AIG / Corebridge',
            bullets: [
              'Additional AIG Form: https://drive.google.com/file/d/1aNPDHNHyFuupWPsV_rIxZHN6vJck6r6P/view?usp=share_link',
              'Beneficiary Change: https://drive.google.com/file/d/1ZL4aoQstYBnAFngjA8fxBtVDwL3yOrZf/view?usp=sharing',
              'Surrender Form: https://drive.google.com/file/d/1S8-fwsO6APlDcna8u5b2_9ZWskmidUUM/view?usp=sharing',
              'Bank Draft: https://drive.google.com/file/d/1nWqCjd1BlfpoffyjZMo9q41RGIcQcQcy/view?usp=share_link',
              'Notice Regarding Replacement: https://drive.google.com/file/d/1Kx9AnAZBBMmLSl0Et7TAaWZLN_SrtS7H/view?usp=sharing',
              'Underwriting Authorization Form: https://drive.google.com/file/d/1qsdwwpC2lZG7XIOS1GrMxqb0IMNFYsS4/view?usp=share_link',
              'Service Request: https://drive.google.com/file/d/1vYs6geg7mtCvd0HVwkvbML1Kg_3dfr7Z/view?usp=sharing',
              'Card Authorization: https://drive.google.com/file/d/1CxpQFuXyon7A9SBv7UBWtfhGKnQ7c1Wd/view?usp=sharing',
            ],
          },
          {
            heading: 'Mutual of Omaha (MOO)',
            bullets: [
              'Payment Authorization Form: https://drive.google.com/file/d/1rDq2K3lw7G0QHl3LRVR6ZVSwaoECuN2d/view?usp=sharing',
              'Change of Owner Form: https://drive.google.com/file/d/1uD1WpTWRqXTj2CI5psaNJAA9Z3F4-C1O/view?usp=sharing',
              'Surrender Form: https://drive.google.com/file/d/11uaqAt_W8B2GMcPVVU92ePB7aycGXDjs/view?usp=sharing',
              'Beneficiary Change Form: https://drive.google.com/file/d/1VAMWorGI6qIG69tUJVaxi3J9Kxcdld3I/view?usp=sharing',
              'Address Change Form: https://drive.google.com/file/d/18uBtMLyAOCMo9D7i0x-F8-Z5HEWVCY0h/view?usp=sharing',
              'Replacement Form: https://drive.google.com/file/d/1Qi1TgFKSRuyQN_jOYyCsW7acLT9R9bm2/view?usp=share_link',
            ],
          },
          {
            heading: 'Transamerica',
            bullets: [
              'Beneficiary Change Form: https://drive.google.com/file/d/11tmE9s-DF7wy67KPvRiTtJ3NO_W88IzD/view?usp=sharing',
              'Ownership Change Form: https://drive.google.com/file/d/145YzguVXf4TX4DI1zc6-AWsaRB2JWY9P/view?usp=share_link',
              'Power of Attorney Form: https://drive.google.com/file/d/16sOLtINPDhq2LYV1aCv3A_H3Rxp28fcq/view?usp=share_link',
              'Replacement Form: https://drive.google.com/file/d/1zt4h7V_P0-v5jz8BP0l3I3L-2AWyFK0l/view?usp=sharing',
              'Bank Draft Form: https://drive.google.com/file/d/1YmgRCrZ41VJ5Opv817q2Ei38ZY0Qz9rj/view?usp=sharing',
              'Individual Life Insurance Policy Change Application: https://drive.google.com/file/d/1WtvEiuNOO2Ch7j4i6pQhE9gDS3Jg51YS/view?usp=share_link',
              'Payment Authorization Form: https://drive.google.com/file/d/15VWacqWweWr6JsRAwdM1B-LthIQ6PZmi/view?usp=sharing',
              'Payor Form: https://drive.google.com/file/d/1Nzo0X1gsLfXvbzkbKjC-_VfizoEx3mD3/view?usp=sharing',
              'Immediate Solution Surrender Form: https://drive.google.com/file/d/1f4aI1CRS1WS6jMyPh4deRORW0wBy_VhM/view?usp=sharing',
              'Immediate Solution RPU Form: https://drive.google.com/file/d/1NUaltcMa6CBLaOcz4Gp8PtvxoJpHdHTf/view?usp=sharing',
              'FE Annotated Application: https://drive.google.com/file/d/1DxKIAs4e00zH_kvoBicXpcWqA-hTJ7Ds/view?usp=sharing',
            ],
          },
          {
            heading: 'Foresters',
            bullets: [
              'Transfer of Ownership: https://drive.google.com/file/d/13DqSKLymdhWHHDxD9pcMVsNou2DcixG6/view?usp=sharing',
              'Bank Authorization: https://drive.google.com/file/d/1SAcvBaVo_1lgR14aeAhyMUA25GLD7zHR/view?usp=sharing',
              'Contingent Owner Form: https://drive.google.com/file/d/1wPdPLHMziO6iFnmNyQOWGImLu6yVL1Z2/view?usp=sharing',
              'Beneficiary Change Form: https://drive.google.com/file/d/1LKtWJkPdFPzuF8Iw6oolGm45MU0BC2Ek/view?usp=sharing',
              'Replacement Form: https://drive.google.com/file/d/1b_-86EP3A3iqrLUQc-nETEOvNkIvJSGV/view?usp=sharing',
              'Certificate Change Form: https://drive.google.com/file/d/14KSnNavErD1uafO7eycYQmvtNii7DIhC/view?usp=sharing',
              'Surrender Form: https://drive.google.com/file/d/1lpECuKg3kJ9L2qsjlX5sBiCVJ4LUXcZI/view?usp=sharing',
            ],
          },
          {
            heading: 'American Amicable (AMAM)',
            bullets: [
              'Beneficiary Change Form: https://drive.google.com/file/d/1FZaA1gOZj6NLjWNDfHMTKOTsMn5fhLtN/view?usp=share_link',
              'Replacement Form: https://drive.google.com/file/d/1jozJF5uStps1yhxNy-kIClZwfq5Z8v1l/view?usp=sharing',
              'Senior Choice Full Application: https://drive.google.com/file/d/1cOfOHe37jdA_Jh1V62MxgO0-z-EMtra2/view?usp=sharing',
              'Policy Change Form: https://drive.google.com/file/d/11P8E6D3OHEdn081xgVzyRDhrdCkb-ikR/view?usp=sharing',
              'Surrender Form: https://drive.google.com/file/d/1gScZspwH3iNLWsepZZ-F6fFDwCRoRS6G/view?usp=sharing',
              'Name Change Request: https://drive.google.com/file/d/1SfIAjalZ387l08yPwrmmRLNYFxFToeMj/view?usp=share_link',
            ],
          },
          {
            heading: 'Americo',
            bullets: [
              'Beneficiary Change Form: https://drive.google.com/file/d/1_ABuvd3qP7TDR96ll7CdB8ivkI2jGtQZ/view?usp=sharing',
              'Owner Change Form: https://drive.google.com/file/d/1fIoN8Hf3F3XvMb3l-1pE9kiJRCQ6tvml/view?usp=share_link',
              'Address Change: https://drive.google.com/file/d/1plgKTnsiHSe6mhfwpBdzAPr7dYL1qyZL/view?usp=sharing',
              'Name Change Request: https://drive.google.com/file/d/1QbuyNR7pa1hY43ooQF-PuwApt-jf7ItD/view?usp=sharing',
              'Bank Draft Authorization: https://drive.google.com/file/d/1v86whcOPuallyJBq9d9mKWfyeDtMCcPw/view?usp=sharing',
            ],
          },
          {
            heading: 'Royal Neighbors',
            bullets: [
              'Replacement Notice: https://drive.google.com/file/d/1E6o21LcxV1Yc20kgtuv0zSB6yKRNDQW_/view?usp=sharing',
              'Service Request (Cash Surrender/RPU/etc.): https://drive.google.com/file/d/1YkLNsv7SLwnZTq-3jo3kRrUOYN2O1KDA/view?usp=sharing',
              'Information Change Request Form: https://drive.google.com/file/d/1zf5HNBMSkXXduQIEVGTClSf7zzxxCY-R/view?usp=sharing',
              'Surrender Form: https://drive.google.com/file/d/1g0c17l0wDFpDQqgH3amCvJOkCr091iSO/view?usp=sharing',
              'Beneficiary Change Form: https://drive.google.com/file/d/1pa1JbMllMnZCdfP4PrliaWkg6pOmmYwA/view?usp=sharing',
              'Bank Authorization Form: https://drive.google.com/file/d/1MirWTPrL2xNel7AQo_6U1NMVsTxGCvxr/view?usp=share_link',
            ],
          },
          {
            heading: 'Liberty Bankers Life (LBL)',
            bullets: [
              'Bank Draft Form: https://drive.google.com/file/d/102B2_X7q3sK0jllWR-UHq4SfHsIHObds/view?usp=sharing',
              'Service Request: https://drive.google.com/file/d/1l0HMT84uq1IR5RXmlxbGDb5CI0d6iZOZ/view?usp=sharing',
            ],
          },
          {
            heading: 'Aetna',
            paragraphs: ['Beneficiary Change Form — link pending.'],
            bullets: [
              'Surrender Form: https://drive.google.com/file/d/1w82vZZZtNHxL1ZgYPib6uLRGZV2jNLdM/view?usp=share_link',
            ],
          },
        ],
      },
    ],
  },
]
